import { useState, useCallback, useRef } from 'react';
import { useI18n } from '../i18n';
import * as THREE from 'three';
import { geodeticToENU } from '../utils/geodetic';
import { POIData } from '../components/POIOverlay';
import { mtm8ToGeodetic } from '../utils/measureUtils';
import { findNearestNavPoseWithDebug } from '../utils/navPoseUtils';
import {
  computePoiLandingFromPathNetwork,
  type GeoPos,
} from '../utils/poiUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SpeedRefs {
  originalMovementSpeedRef: React.MutableRefObject<number>;
  originalLookSpeedRef: React.MutableRefObject<number>;
  originalMaxClickDistRef: React.MutableRefObject<number>;
}

interface UsePoiFlythroughArgs {
  viewerRef: React.MutableRefObject<any>;
  pois: POIData[];
  speedRefs: SpeedRefs;
  pathNetwork?: number[][][];
  getCurrentSegment?: () => number | null;
  getManifestSegmentForPoi?: (poi: POIData) => number | null;
}

export interface UsePoiFlythroughReturn {
  flyTo: (id: string) => Promise<void>;
  flyToGeoPoint: (target: {
    latitude: number;
    longitude: number;
    altitude?: number;
    name?: string;
  }) => Promise<void>;
  isNavigating: boolean;
  navigatingToPoiName: string | null;
  selectedPoiId: string | null;
  setSelectedPoiId: (id: string | null) => void;
  /** Ref — readable synchronously by segment callbacks to skip their freeze/restore */
  isNavigatingRef: React.MutableRefObject<boolean>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Horizontal camera standoff distance from the POI */
const STANDOFF_DISTANCE = 9;
/** Eye-level height added above the POI's resolved altitude */
const EYE_LEVEL_OFFSET  = 1.6;
/** Inset from segment box edges when clamping — avoids landing exactly on boundary */
const SEGMENT_BOX_INSET = 0.5;

/**
 * Resolves the best geodetic position for a POI.
 * Prefers MTM8-projected points (more accurate) over raw lat/lon.
 */
function resolveGeoPosition(poi: POIData): [number, number, number] {
  if (poi.points?.[0]) {
    const p = poi.points[0];
    if (Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)) {
      const geo = mtm8ToGeodetic(p.x, p.y, p.z);
      return [geo.latitude, geo.longitude, geo.altitude];
    }
  }
  return [poi.latitude, poi.longitude, poi.altitude];
}

/**
 * Converts a POI's resolved geodetic position to a THREE.Vector3 world position,
 * using the viewer's observerCoordinates and the ENU→world quaternion.
 * Returns null if observerCoordinates aren't ready yet.
 */
function poiToWorldPos(poi: POIData, viewer: any): THREE.Vector3 | null {
  const obs = viewer.observerCoordinates as [number, number, number] | undefined;
  if (!obs || obs.length < 3) return null;
  const [lat, lon, alt] = resolveGeoPosition(poi);
  const { east, north, up } = geodeticToENU(lat, lon, alt, obs[0], obs[1], obs[2]);
  const world = new THREE.Vector3(east, north, up);
  world.applyQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));
  return world;
}

/**
 * Computes a good landing position relative to the POI:
 * - STANDOFF_DISTANCE metres back along the approach vector
 * - EYE_LEVEL_OFFSET metres above the POI's Y position
 *
 * When camera is already on top of the POI (distance < 1cm), falls back to the
 * inverted camera forward direction projected on XZ — preserving user orientation
 * rather than picking an arbitrary east-west axis.
 */
function computeLandingPosition(
  poiWorldPos: THREE.Vector3,
  cameraPos: THREE.Vector3,
  cameraQuaternion: THREE.Quaternion,
): THREE.Vector3 {
  const toPOI = poiWorldPos.clone().sub(cameraPos);
  toPOI.y = 0;

  let approachDir: THREE.Vector3;

  if (toPOI.lengthSq() > 0.01) {
    approachDir = toPOI.normalize();
  } else {
    // Camera is essentially on the POI — use opposite of current facing
    approachDir = new THREE.Vector3(0, 0, -1).applyQuaternion(cameraQuaternion);
    approachDir.y = 0;

    if (approachDir.lengthSq() > 0.01) {
      approachDir.normalize().negate();
    } else {
      approachDir.set(1, 0, 0);
    }
  }

  console.log('[PoiFlythrough] chosen approachDir XZ:', approachDir.x, approachDir.z);

    const desiredY = poiWorldPos.y + EYE_LEVEL_OFFSET;
    const minAllowedY = cameraPos.y - 2.0 ; // tune this
    const landingY = Math.max(desiredY, minAllowedY);

    console.log('[PoiFlythrough] desiredY:', desiredY);
    console.log('[PoiFlythrough] minAllowedY:', minAllowedY);
    console.log('[PoiFlythrough] chosen landingY:', landingY);

    return new THREE.Vector3(
    poiWorldPos.x - approachDir.x * STANDOFF_DISTANCE,
    landingY,
    poiWorldPos.z - approachDir.z * STANDOFF_DISTANCE,
    );
}

/**
 * Clamps a landing position to stay inside a segment's viewBoundingBox,
 * with a small inset to avoid landing exactly on the boundary.
 * Y (eye level) is clamped separately so it stays within the box height too.
 * Returns the original position unchanged if no segment or box is found.
 */
function clampToSegmentBox(
  pos: THREE.Vector3,
  viewer: any,
  segmentIndex: number | null,
): THREE.Vector3 {
  if (segmentIndex === null) return pos;
  const segment = viewer.lazyLoadSegments?.get(segmentIndex);
  const box = segment?.viewBoundingBox as THREE.Box3 | undefined;
  if (!box) return pos;

  const inset = SEGMENT_BOX_INSET;
  function safeClamp(value: number, min: number, max: number, inset: number) {
    const a = min + inset;
    const b = max - inset;
    return THREE.MathUtils.clamp(
        value,
        Math.min(a, b),
        Math.max(a, b)
    );
  }
  
    return new THREE.Vector3(
        safeClamp(pos.x, box.min.x, box.max.x, inset),
        pos.y,
        safeClamp(pos.z, box.min.z, box.max.z, inset),
    );
}

/**
 * Points the camera at a world-space target and syncs the controls,
 * so the user is facing the POI the moment the overlay disappears.
 */
function lookAtWorldPos(viewer: any, target: THREE.Vector3): void {
  if (!viewer.camera) return;
  viewer.camera.lookAt(target);
  viewer.camera.updateMatrixWorld();
  viewer.controls?.updateLookDirection?.();
}

/**
 * Freezes all viewer input: keyboard movement, mouse look, click-to-move.
 * Saves real current values so restore is exact — no hardcoded fallbacks.
 * Returns a restore function to call in finally.
 */
function freezeViewerControls(viewer: any, speedRefs: SpeedRefs): () => void {
  if (viewer.controls) {
    speedRefs.originalMovementSpeedRef.current = viewer.controls.movementSpeed ?? 5;
    speedRefs.originalLookSpeedRef.current     = viewer.controls.lookSpeed     ?? 10;
    viewer.controls.movementSpeed = 0;
    viewer.controls.lookSpeed     = 0;
  }

  speedRefs.originalMaxClickDistRef.current = viewer.maxClickToMoveDistance ?? 15;
  viewer.maxClickToMoveDistance = 0;

  // Cancel any in-flight click-to-move transition
  viewer.clickTransitioning        = false;
  viewer.transitioningCameraTarget = false;
  if (viewer.nextCamera)     viewer.nextCamera.copy(viewer.camera);
  if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);

  return () => {
    if (viewer.controls) {
      viewer.controls.movementSpeed = speedRefs.originalMovementSpeedRef.current;
      viewer.controls.lookSpeed     = speedRefs.originalLookSpeedRef.current;
    }
    viewer.maxClickToMoveDistance = speedRefs.originalMaxClickDistRef.current;
  };
}

/**
 * Waits two animation frames — enough for React to flush state + renderer
 * to paint one frame with the new camera position and POI overlay.
 */
function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function formatWorld(v: THREE.Vector3 | null | undefined): string {
  if (!v) return 'null';
  return `[${v.x.toFixed(3)}, ${v.y.toFixed(3)}, ${v.z.toFixed(3)}]`;
}

function findContainingSegmentForWorldPos(
  viewer: any,
  worldPos: THREE.Vector3 | null,
): number | null {
  if (!viewer?.lazyLoadSegments || !worldPos) return null;

  for (const segment of viewer.lazyLoadSegments.values()) {
    if (segment?.viewBoundingBox?.containsPoint(worldPos)) {
      return segment.index;
    }
  }

  return null;
}

function closestPointOnSegment2D(
  pLon: number,
  pLat: number,
  aLon: number,
  aLat: number,
  bLon: number,
  bLat: number,
): { longitude: number; latitude: number; t: number; distSq: number } {
  const abLon = bLon - aLon;
  const abLat = bLat - aLat;
  const apLon = pLon - aLon;
  const apLat = pLat - aLat;

  const abLenSq = abLon * abLon + abLat * abLat;
  const t = abLenSq > 0 ? Math.max(0, Math.min(1, (apLon * abLon + apLat * abLat) / abLenSq)) : 0;

  const longitude = aLon + abLon * t;
  const latitude = aLat + abLat * t;

  const dLon = pLon - longitude;
  const dLat = pLat - latitude;
  const distSq = dLon * dLon + dLat * dLat;

  return { longitude, latitude, t, distSq };
}

function snapGeoToPathNetwork(
  target: GeoPos,
  pathNetwork?: number[][][],
): GeoPos | null {
  if (!pathNetwork || pathNetwork.length === 0) return null;

  let best: GeoPos | null = null;
  let bestDistSq = Infinity;

  for (const segment of pathNetwork) {
    if (!segment || segment.length === 0) continue;

    if (segment.length === 1) {
      const [lon, lat] = segment[0];
      const dLon = target.longitude - lon;
      const dLat = target.latitude - lat;
      const distSq = dLon * dLon + dLat * dLat;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        best = { latitude: lat, longitude: lon };
      }
      continue;
    }

    for (let i = 0; i < segment.length - 1; i++) {
      const [aLon, aLat] = segment[i];
      const [bLon, bLat] = segment[i + 1];

      const candidate = closestPointOnSegment2D(
        target.longitude,
        target.latitude,
        aLon,
        aLat,
        bLon,
        bLat,
      );

      if (candidate.distSq < bestDistSq) {
        bestDistSq = candidate.distSq;
        best = {
          latitude: candidate.latitude,
          longitude: candidate.longitude,
        };
      }
    }
  }

  return best;
}

/**
 * Polls predicate every intervalMs until it returns true or timeoutMs elapses.
 * Returns true if predicate passed, false if timed out.
 */
async function waitUntil(
  predicate: () => boolean,
  timeoutMs = 5000,
  intervalMs = 80,
): Promise<boolean> {
  const deadline = performance.now() + timeoutMs;
  while (performance.now() < deadline) {
    if (predicate()) return true;
    await new Promise<void>((r) => setTimeout(r, intervalMs));
  }
  return false;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePoiFlythrough({
  viewerRef,
  pois,
  speedRefs,
  pathNetwork,
  getCurrentSegment,
  getManifestSegmentForPoi,
}: UsePoiFlythroughArgs): UsePoiFlythroughReturn {
  const { t } = useI18n();
  const [isNavigating, setIsNavigating]               = useState(false);
  const [navigatingToPoiName, setNavigatingToPoiName] = useState<string | null>(null);
  const [selectedPoiId, setSelectedPoiId]             = useState<string | null>(null);

  // Monotonic token — incremented on every new flyTo call.
  // Any async step that sees its token superseded exits silently.
  const navTokenRef     = useRef(0);

  // Exposed as a ref so segment-loading callbacks can read synchronously
  // without stale closure issues.
  const isNavigatingRef = useRef(false);

  const flyTo = useCallback(async (id: string) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const poi = pois.find((p) => p.id === id);
    if (!poi) return;

    const myToken       = ++navTokenRef.current;
    const targetSegment = getManifestSegmentForPoi?.(poi) ?? null;

    // ── Resolve world position of the POI (what we look at) ──────────────────
    const poiWorldPos    = poiToWorldPos(poi, viewer);
    const cameraPos      = (viewer.camera?.position?.clone() as THREE.Vector3) ?? new THREE.Vector3();
    const cameraQuat     = (viewer.camera?.quaternion?.clone() as THREE.Quaternion) ?? new THREE.Quaternion();

    // ── Compute landing position (where camera goes) ──────────────────────────
    // If world coords available: standoff + clamp to segment box.
    // If not: geo fallback with eye-level only (no 3D standoff possible without world coords).
        
    const [lat, lon, alt] = resolveGeoPosition(poi);
    let landingPos: THREE.Vector3 | null = null;

    if (poiWorldPos) {
    const poiGeo: GeoPos = {
        latitude: lat,
        longitude: lon,
    };

    const geoToWorld = (geo: GeoPos): THREE.Vector3 | null => {
        const obs = viewer.observerCoordinates as [number, number, number] | undefined;
        if (!obs || obs.length < 3) return null;

        const { east, north, up } = geodeticToENU(
        geo.latitude,
        geo.longitude,
        alt,
        obs[0],
        obs[1],
        obs[2]
        );

        const world = new THREE.Vector3(east, north, up);
        world.applyQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));
        return world;
    };

    const pathLanding = pathNetwork?.length
        ? computePoiLandingFromPathNetwork(
            poiGeo,
            poiWorldPos,
            cameraPos,
            pathNetwork,
            geoToWorld,
            {
            standoffDistance: STANDOFF_DISTANCE,
            eyeLevelOffset: EYE_LEVEL_OFFSET,
            maxDropFromCurrent: 2.0,
            }
        )
        : null;

    if (pathLanding) {
        landingPos = clampToSegmentBox(pathLanding.landingWorldPos, viewer, targetSegment);
        console.log('[PoiFlythrough] using path-based landing');
    } else {
        const raw = computeLandingPosition(poiWorldPos, cameraPos, cameraQuat);
        landingPos = clampToSegmentBox(raw, viewer, targetSegment);
        console.log('[PoiFlythrough] using fallback camera->POI landing');
    }
    }

    console.log(
      `[PoiFlythrough] ✈️ Flying to "${poi.name}" ` +
      (landingPos
        ? `→ world [${landingPos.x.toFixed(2)}, ${landingPos.y.toFixed(2)}, ${landingPos.z.toFixed(2)}] (segment ${targetSegment})`
        : `→ geo fallback [${lat.toFixed(6)}, ${lon.toFixed(6)}, ${(alt + EYE_LEVEL_OFFSET).toFixed(2)}] — no world coords yet`),
    );

    // 1. Mark selected + show overlay immediately
    setSelectedPoiId(id);
    setNavigatingToPoiName(poi.name);
    setIsNavigating(true);
    isNavigatingRef.current = true;

    // 2. Freeze all controls
    const restoreControls = freezeViewerControls(viewer, speedRefs);

    try {
      // 3. Teleport — world position (with standoff + clamped to segment) when available,
      //    geodetic fallback otherwise (observerCoordinates not yet set)
      if (landingPos) {
        await viewer.teleportTo({ position: landingPos.toArray() });
      } else {
        await viewer.teleportTo({ geoPosition: [lat, lon, alt + EYE_LEVEL_OFFSET] });
      }

      if (navTokenRef.current !== myToken) return;

      // 4. Orient camera toward the POI (both paths — lookAt is always desirable)
      if (poiWorldPos) {
        lookAtWorldPos(viewer, poiWorldPos);
      }

      // 5. Wait until camera is confirmed inside the target segment.
      //    Capture the result — a false means timeout, not arrival.
      const reachedTargetSegment = await waitUntil(() => {
        if (navTokenRef.current !== myToken) return true; // abort wait on race
        if (targetSegment === null)          return true; // no segment info — skip
        return getCurrentSegment?.() === targetSegment;
      }, 5000, 80);

      if (!reachedTargetSegment && targetSegment !== null) {
        console.warn(
        `[PoiFlythrough] ⚠️ Timed out waiting for segment ${targetSegment} — ` +
        `camera is in segment ${getCurrentSegment?.()} instead.`
        );
      }

      if (navTokenRef.current !== myToken) return;

      // 6. Extra paint so POI pin overlay has rendered before overlay hides
      await waitForNextPaint();

    } catch (err) {
      console.warn('[PoiFlythrough] teleportTo failed:', err);
    } finally {
      // Only the token owner clears state — stale completions are ignored
      if (navTokenRef.current === myToken) {
        restoreControls();
        isNavigatingRef.current = false;
        setIsNavigating(false);
        setNavigatingToPoiName(null);
      }
    }
  }, [viewerRef, pois, pathNetwork, speedRefs, getCurrentSegment, getManifestSegmentForPoi]);

    const flyToGeoPoint = useCallback(async (target: {
    latitude: number;
    longitude: number;
    altitude?: number;
    name?: string;
  }) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const myToken = ++navTokenRef.current;

    const snappedGeo = snapGeoToPathNetwork(
      {
        latitude: target.latitude,
        longitude: target.longitude,
      },
      pathNetwork,
    ) ?? {
      latitude: target.latitude,
      longitude: target.longitude,
    };

    const fallbackAlt =
      target.altitude ??
      viewer.getCameraGeodeticPosition?.()?.alt ??
      0;

    const geoToWorld = (geo: GeoPos): THREE.Vector3 | null => {
      const obs = viewer.observerCoordinates as [number, number, number] | undefined;
      if (!obs || obs.length < 3) return null;

      const { east, north, up } = geodeticToENU(
        geo.latitude,
        geo.longitude,
        fallbackAlt,
        obs[0],
        obs[1],
        obs[2],
      );

      const world = new THREE.Vector3(east, north, up);
      world.applyQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));
      return world;
    };

    const targetWorldPos = geoToWorld(snappedGeo);
    let landingPos: THREE.Vector3 | null = targetWorldPos;
    let usedNearestNavPose = false;

    console.log('[PoiFlythrough] map click raw geo:', target.latitude, target.longitude);
    console.log('[PoiFlythrough] map click snapped geo:', snappedGeo.latitude, snappedGeo.longitude);

        if (targetWorldPos) {
      try {
        const poseDebug = await findNearestNavPoseWithDebug({
          x: targetWorldPos.x,
          y: targetWorldPos.y,
          z: targetWorldPos.z,
        });

        const nearestPose = poseDebug.nearest;

        console.log('[PoiFlythrough] map click snapped world:', formatWorld(targetWorldPos));
        console.log('[PoiFlythrough] map click nearest pose distance:', poseDebug.nearestDistance);

        console.log(
          '[PoiFlythrough] map click top 3 nearest poses:',
          poseDebug.topCandidates.map((c) => ({
            id: c.id,
            name: c.name,
            distance: Number(c.distance.toFixed(3)),
            worldPos: [
              Number(c.worldPos.x.toFixed(3)),
              Number(c.worldPos.y.toFixed(3)),
              Number(c.worldPos.z.toFixed(3)),
            ],
          })),
        );

        if (nearestPose) {
          landingPos = new THREE.Vector3(
            nearestPose.worldPos.x,
            nearestPose.worldPos.y,
            nearestPose.worldPos.z,
          );
          usedNearestNavPose = true;

          console.log('[PoiFlythrough] map click nearest pose id:', nearestPose.id);
          console.log('[PoiFlythrough] map click nearest pose world:', formatWorld(landingPos));
        } else {
          console.log('[PoiFlythrough] map click nearest pose id:', 'none');
        }
      } catch (error) {
        console.warn('[PoiFlythrough] nav pose lookup failed:', error);
      }
    }

    const label = target.name ?? t.poiNav.selectedPoint;

        if (landingPos) {
      const cameraWorld = viewer.camera?.position?.clone?.() ?? null;
      const snappedSegment = findContainingSegmentForWorldPos(viewer, targetWorldPos);
      const landingSegment = findContainingSegmentForWorldPos(viewer, landingPos);

      console.log('[PoiFlythrough] map click camera world before teleport:', formatWorld(cameraWorld));
      console.log('[PoiFlythrough] map click snapped world segment:', snappedSegment);
      console.log('[PoiFlythrough] map click landing world segment:', landingSegment);

      console.log(
        '[PoiFlythrough] map click final teleport world:',
        landingPos.x,
        landingPos.y,
        landingPos.z,
      );
    }

    setSelectedPoiId(null);
    setNavigatingToPoiName(label);
    setIsNavigating(true);
    isNavigatingRef.current = true;

    const restoreControls = freezeViewerControls(viewer, speedRefs);

    try {
      if (landingPos) {
        await viewer.teleportTo({ position: landingPos.toArray() });
      } else {
        await viewer.teleportTo({
          geoPosition: [
            snappedGeo.latitude,
            snappedGeo.longitude,
            fallbackAlt + EYE_LEVEL_OFFSET,
          ],
        });
      }

      if (navTokenRef.current !== myToken) return;

      if (targetWorldPos && !usedNearestNavPose) {
        lookAtWorldPos(viewer, targetWorldPos);
      }

      await waitForNextPaint();
    } catch (err) {
      console.warn('[PoiFlythrough] map click teleport failed:', err);
    } finally {
      if (navTokenRef.current === myToken) {
        restoreControls();
        isNavigatingRef.current = false;
        setIsNavigating(false);
        setNavigatingToPoiName(null);
      }
    }
  }, [viewerRef, pathNetwork, speedRefs, t.poiNav.selectedPoint]);

  return {
    flyTo,
    flyToGeoPoint,
    isNavigating,
    navigatingToPoiName,
    selectedPoiId,
    setSelectedPoiId,
    isNavigatingRef,
  };
}
